import { useOrders, useSettings } from '@restaurant/api-client/react';
import { formatMoney, ORDER_STATUS_LABEL, type ConversationDto, type CustomerDto } from '@restaurant/shared';
import { format } from 'date-fns';
import { Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, Input, Textarea } from '@/components/ui/input';
import { Spinner, Switch } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { formatPhone } from '@/lib/utils';
import { CreateOrderDialog } from '../orders/CreateOrderDialog';
import { OrderActions } from '../orders/OrderActions';
import { STATUS_STYLE } from '../orders/format';
import { INTENT_LABEL } from './format';
import { CustomerInsights } from './CustomerInsights';

export function CustomerPanel({ conversation: c, onClose }: { conversation: ConversationDto; onClose: () => void }) {
  const { data: settings } = useSettings();
  const orders = useOrders({ customerId: c.customer.id, limit: 20 });
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: c.customer.name ?? '', defaultAddress: c.customer.defaultAddress ?? '', notes: c.customer.notes ?? '' });
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const currency = settings?.currency ?? 'LKR';
  const draft = orders.data?.find((o) => o.status === 'draft');
  const dirty = form.name !== (c.customer.name ?? '') || form.defaultAddress !== (c.customer.defaultAddress ?? '') || form.notes !== (c.customer.notes ?? '');

  useEffect(() => setForm({ name: c.customer.name ?? '', defaultAddress: c.customer.defaultAddress ?? '', notes: c.customer.notes ?? '' }), [c.customer.id, c.customer.name, c.customer.defaultAddress, c.customer.notes]);

  const save = async () => {
    setSaving(true);
    try {
      await api.customers.update(c.customer.id, { name: form.name || null, defaultAddress: form.defaultAddress || null, notes: form.notes || null });
      toast.success('Customer saved');
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const extract = async () => {
    setExtracting(true);
    try {
      const r = await api.conversations.extractOrder(c.id);
      if (r.status === 'no_items') toast.info('No menu items found in this chat', { description: r.unclear?.join(' · ') });
      else if (r.status === 'not_an_order') toast.info('This chat does not look like an order');
      else toast.success(r.status === 'draft_updated' ? 'Draft updated' : 'Draft order created');
      void orders.refetch();
    } catch (err) {
      toastError(err);
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <p className="font-medium">Customer</p>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-accent" aria-label="Close panel"><X className="size-4" /></button>
      </div>
      <div className="space-y-5 p-4">
        <div>
          <p className="text-sm text-muted-foreground">{formatPhone(c.customer.waId)}{c.customer.profileName ? ` · WhatsApp name "${c.customer.profileName}"` : ''}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {c.aiIntent ? <Badge variant={INTENT_LABEL[c.aiIntent]?.variant}>{INTENT_LABEL[c.aiIntent]?.label}</Badge> : null}
            {c.aiSentiment !== null ? <Badge variant={c.aiSentiment < -0.2 ? 'destructive' : c.aiSentiment > 0.2 ? 'success' : 'muted'}>Mood {c.aiSentiment > 0 ? '+' : ''}{c.aiSentiment.toFixed(1)}</Badge> : null}
            <Badge variant="outline">{c.customer.orderCount} order{c.customer.orderCount === 1 ? '' : 's'}</Badge>
          </div>
        </div>

        <MarketingConsent customer={c.customer} />

        {/* AI draft card: staff confirm, edit or discard (AI never confirms on its own). */}
        <section className="rounded-xl border border-dashed p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-sm font-semibold"><Sparkles className="size-4 text-primary" /> Order draft</p>
            <Button size="sm" variant="ghost" onClick={() => void extract()} disabled={extracting}>
              {extracting ? <Spinner /> : null} {draft ? 'Re-read chat' : 'Extract with AI'}
            </Button>
          </div>
          {draft ? (
            <div className="space-y-2">
              <ul className="text-sm">
                {draft.items.map((i) => <li key={i.id}><span className="tabular-nums">{i.quantity}×</span> {i.name}{i.options.length ? <span className="text-muted-foreground"> ({i.options.map((o) => o.name).join(', ')})</span> : null}</li>)}
              </ul>
              <p className="text-sm">{draft.type === 'delivery' ? `Delivery${draft.address ? ` · ${draft.address}` : ' · no address yet'}` : 'Pickup'} · <b>{formatMoney(draft.totalCents, currency)}</b></p>
              {draft.aiUnclear.length ? <ul className="list-disc rounded-md bg-warning/10 py-1.5 pl-6 pr-2 text-xs">{draft.aiUnclear.map((u, i) => <li key={i}>{u}</li>)}</ul> : null}
              <div className="flex flex-wrap items-center gap-2">
                <OrderActions order={draft} />
                <Button size="sm" variant="ghost" onClick={() => navigate(`/orders?order=${draft.id}`)}>Edit</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No draft. AI creates one automatically when the customer places an order, or take it manually.</p>
          )}
          <div className="mt-3"><CreateOrderDialog conversationId={c.id} defaultAddress={c.customer.defaultAddress} onCreated={() => void orders.refetch()} /></div>
        </section>

        <CustomerInsights customer={c.customer} currency={currency} />

        <section className="grid gap-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={c.customer.profileName ?? ''} /></Field>
          <Field label="Default delivery address"><Textarea rows={2} value={form.defaultAddress} onChange={(e) => setForm({ ...form, defaultAddress: e.target.value })} /></Field>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Allergies, preferences…" /></Field>
          {dirty ? <Button size="sm" onClick={() => void save()} disabled={saving}>{saving ? <Spinner /> : null} Save customer</Button> : null}
        </section>

        <section>
          <p className="mb-2 text-sm font-semibold">Recent orders</p>
          {orders.data?.filter((o) => o.status !== 'draft').length ? (
            <ul className="space-y-1.5">
              {orders.data.filter((o) => o.status !== 'draft').map((o) => (
                <li key={o.id}>
                  <button onClick={() => navigate(`/orders?order=${o.id}`)} className="grid w-full grid-cols-[1fr_auto] items-center gap-x-2 rounded-md border px-2.5 py-1.5 text-left text-sm hover:bg-accent">
                    <span className="truncate">#{o.number} · {format(new Date(o.createdAt), 'dd MMM')}</span>
                    <Badge variant={STATUS_STYLE[o.status].variant}>{ORDER_STATUS_LABEL[o.status]}</Badge>
                    <span className="text-xs text-muted-foreground tabular-nums">{formatMoney(o.totalCents, currency)}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">No orders yet.</p>}
        </section>
      </div>
    </div>
  );
}

const CONSENT_LABEL: Record<CustomerDto['marketingStatus'], string> = {
  opted_in: 'Receives promotions',
  opted_out: 'Unsubscribed from promotions',
  invited: 'Invited, no answer yet',
  unknown: 'Not asked about promotions yet',
};

/** Promotions consent. Customers change it themselves with STOP / START; staff can record a verbal yes/no. */
function MarketingConsent({ customer }: { customer: CustomerDto }) {
  const [busy, setBusy] = useState(false);
  const on = customer.marketingStatus === 'opted_in';
  const toggle = async (optIn: boolean) => {
    setBusy(true);
    try {
      await api.customers.setMarketing(customer.id, optIn);
      toast.success(optIn ? 'Customer will receive promotions' : 'Customer unsubscribed from promotions');
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="flex items-center justify-between gap-3 rounded-xl border p-3">
      <div>
        <p className="text-sm font-semibold">Promotions</p>
        <p className="text-xs text-muted-foreground">
          {CONSENT_LABEL[customer.marketingStatus]}
          {customer.marketingUpdatedAt ? ` · ${format(new Date(customer.marketingUpdatedAt), 'd MMM')}` : ''}
        </p>
      </div>
      <Switch checked={on} disabled={busy} onCheckedChange={(v) => void toggle(v)} aria-label="Receives promotions" />
    </section>
  );
}
